import api from '../api/client';

const STATE = {
	INITIAL:     'initial',
	INITIALIZED: 'initialized',
};

const EVENT = {
	// base
	INIT:                        'init',
	CASTING_START:               'cast-start',
	CASTING_STOP:                'cast-stop',
	PROGRESS:                    'progress',
	// remote events
	ANY_CHANGE:                  'anyChanged',
	IS_CONNECTED_CHANGED:        'isConnectedChanged',
	IS_MEDIA_LOADED_CHANGED:     'isMediaLoadedChanged',
	DURATION_CHANGED:            'durationChanged',
	CURRENT_TIME_CHANGED:        'currentTimeChanged',
	IS_PAUSED_CHANGED:           'isPausedChanged',
	VOLUME_LEVEL_CHANGED:        'volumeLevelChanged',
	CAN_CONTROL_VOLUME_CHANGED:  'canControlVolumeChanged',
	IS_MUTED_CHANGED:            'isMutedChanged',
	CAN_PAUSE_CHANGED:           'canPauseChanged',
	CAN_SEEK_CHANGED:            'canSeekChanged',
	DISPLAY_NAME_CHANGED:        'displayNameChanged',
	STATUS_TEXT_CHANGED:         'statusTextChanged',
	TITLE_CHANGED:               'titleChanged',
	DISPLAY_STATUS_CHANGED:      'displayStatusChanged',
	MEDIA_INFO_CHANGED:          'mediaInfoChanged',
	IMAGE_URL_CHANGED:           'imageUrlChanged',
	PLAYER_STATE_CHANGED:        'playerStateChanged',
	LIVE_SEEKABLE_RANGE_CHANGED: 'liveSeekableRange',
};

// Reference:
// - https://developers.google.com/cast/docs/web_sender/integrate
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayer#volumeLevel
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayerController#seek

// const RECEIVER_APP = chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
const RECEIVER_APP = '4EA1EA86';

class Chromecast {
	_state = STATE.INITIAL;
	_player = null;
	_playerController = null;
	_eventListeners = Object.values(EVENT).reduce((acc, event) => {
		// @ts-ignore
		acc[event] = [];
		return acc;
	}, {});

	_sessionMessageSubscriptionMap = new Map();
	_eventId = null;
	_eventPassword = null;

	constructor() {
		const isBlueTv = window.$Helpers && window.$Helpers.isBlueTv();
		if (isBlueTv) return;

		window.__onGCastApiAvailable = (isAvailable) => {
			if (isAvailable) {
				this.init();
			} else {
				console.log('Chromecast is not available on this client.');
			}
		};
		const script = document.createElement('script');
		script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
		document.body.append(script);
	}

	addEventListener(event, handler) {
		this._eventListeners[event].push(handler);

		if (event === EVENT.INIT && this._state !== STATE.INITIAL) {
			handler();
		}
	}

	setEventId(value) {
		this._eventId = value;
	}

	setEventPassword(value) {
		this._eventPassword = value;
	}

	removeEventListener(event, handler) {
		this._eventListeners[event] = this._eventListeners[event].filter((currentHandler) => currentHandler !== handler);
	}

	emit(event, payload) {
		for (const handler of this._eventListeners[event]) {
			handler(payload);
		}
	}

	get isAvailable() {
		return this._state === STATE.INITIALIZED;
	}

	// eslint-disable-next-line class-methods-use-this
	get instance() {
		return cast.framework.CastContext.getInstance();
	}

	get currentSession() {
		return this.instance.getCurrentSession();
	}

	async createSession() {
		await this.instance.requestSession();
		if (!this.currentSession) {
			throw new Error('Casting session was not created.');
		}
		return this.currentSession;
	}

	async connect() {
		const session = this.currentSession ?? (await this.createSession());
		if (!this._sessionMessageSubscriptionMap.has(session)) {
			session.addMessageListener('urn:x-cast:tv.asport', async(namespace, message) => {
				const messageData = JSON.parse(message);
				console.log('[chromecast] @message urn:x-cast:tv.asport', { namespace, message, eventId: this._eventId });
				if (messageData.type === 'PLAYOUT_REQUEST') {
					const userAgent = messageData.data.ua;
					console.log('[chromecast] @message playout request', { userAgent });
					const stream = await api.getEventStream(this._eventId, this._eventPassword, userAgent);
					console.log('[chromecast] @message playout response', { stream });
					const url = stream?.hlsUrl || null;
					session.sendMessage('urn:x-cast:tv.asport', {
						type: 'PLAYOUT_RESOLVE',
						data: { url },
					});
				}
			});
			this._sessionMessageSubscriptionMap.set(session, true);
		}
		return session;
	}

	get player() {
		if (!this._player) throw new Error('Player is not initialized.');
		return this._player;
	}

	get playerController() {
		if (!this._playerController) throw new Error('Player is not initialized.');
		return this._playerController;
	}

	get state() {
		if (this._state === STATE.INITIAL) return STATE.INITIAL;
		return this.player.playerState;
	}

	get isConnected() {
		return this.player.isConnected;
	}

	get isPaused() {
		return this.player.isPaused;
	}

	get isMediaLoaded() {
		return this.player.isMediaLoaded;
	}

	get position() {
		return this.player.currentTime;
	}

	get duration() {
		return this.player.duration;
	}

	play() {
		if (!this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	pause() {
		if (this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	playOrPause() {
		this.playerController.playOrPause();
	}

	seek(position) {
		this.player.currentTime = position;
		this.playerController.seek();
	}

	setVolume(volume) {
		this.player.volumeLevel = volume / 100;
		this.playerController.setVolumeLevel();
	}

	get isMuted() {
		return this.player.isMuted;
	}

	mute() {
		if (this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	unmute() {
		if (!this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	async init() {
		if (!chrome?.cast?.AutoJoinPolicy) return;

		this.instance.setOptions({
			receiverApplicationId: RECEIVER_APP,
			autoJoinPolicy:        chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
		});

		this.instance.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, (event) => {
			console.log('SESSION_STATE_CHANGED', event);
			if (event.sessionState === cast.framework.SessionState.SESSION_STARTED) {
				this.emit(EVENT.CASTING_START);
			}
			if (event.sessionState === cast.framework.SessionState.SESSION_ENDED) {
				this.emit(EVENT.CASTING_STOP);
			}

			if (event.sessionState === cast.framework.SessionState.SESSION_START_FAILED && event.errorCode === chrome.cast.ErrorCode.CANCEL) {
				this.emit(EVENT.CASTING_STOP);
			}
		});

		this._player = new cast.framework.RemotePlayer();
		this._playerController = new cast.framework.RemotePlayerController(this._player);

		// any change
		this._playerController.addEventListener(cast.framework.RemotePlayerEventType.ANY_CHANGE, () => {
			this.emit(EVENT.ANY_CHANGE);
		});

		// media info change (currentTime, metadata)
		this._playerController.addEventListener(cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED, () => {
			if (this.currentSession) {
				const mediaSession = this.currentSession.getMediaSession();
				if (mediaSession) {
					const position = mediaSession.getEstimatedTime();
					this.emit(EVENT.PROGRESS, position);
				}
			}

			this.emit(EVENT.MEDIA_INFO_CHANGED);
		});

		this._playerController.addEventListener(cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED, () => {
			console.log('IS_CONNECTED_CHANGED', this.isConnected);
			if (this.player.isConnected) {
				this.emit(EVENT.CASTING_START);
			} else {
				this.emit(EVENT.CASTING_STOP);
			}
			this.emit(EVENT.IS_CONNECTED_CHANGED);
		});

		this._playerController.addEventListener(cast.framework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED, () => {
			this.emit(EVENT.IS_MEDIA_LOADED_CHANGED);
		});

		this._state = STATE.INITIALIZED;
		this.emit(EVENT.INIT);
	}

	async castMedia(url, { audioTrackId = null, position = 0, contentType = 'application/x-mpegurl' }) {
		try {
			const session = await this.connect();

			const mediaInfo = new chrome.cast.media.MediaInfo(url, contentType);
			mediaInfo.streamType = url.includes('live') ? chrome.cast.media.StreamType.LIVE : chrome.cast.media.StreamType.BUFFERED;

			const request = new chrome.cast.media.LoadRequest(mediaInfo);
			request.autoplay = true;
			request.currentTime = position;

			// TODO pass custom audio track id
			// if (audioTrackId !== null) {
			// 	request.activeTrackIds = [audioTrackId];
			// }
			request.customData = {
				...(audioTrackId ? { audioTrackId } : {}),
			};

			await session.loadMedia(request);
		} catch {
			this.emit(EVENT.CASTING_STOP);
		}
	}

	stopCasting() {
		this.playerController.stop();
		if (!this.currentSession) return;
		this.currentSession.endSession(true);
	}
}

export { Chromecast, EVENT };
export default Chromecast;
